/**
 * AdminKycPanel — Iter 155
 *
 * Full admin mirror of a user's KYC state + actions:
 *   • Full wyzia_kyc payload (status, request_id, account_details, raw_response)
 *   • Append-only audit timeline (every state transition)
 *   • 4 mutation actions: Approve / Reject / Reset / Enforce
 *
 * Lives inside `AdminAccount360Drawer` Business tab, and is also mountable
 * on a future dedicated `/admin/kyc/{user_id}` console page.
 */
import { useEffect, useState } from "react";
import {
  ShieldCheck, Check, X, RefreshCw, AlertCircle, Lock, ExternalLink,
  ChevronDown, ChevronRight, Clock, User as UserIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TONE = {
  SUCCESS:     "emerald",
  COMPLETED:   "emerald",
  INITED:      "amber",
  PROCESSING:  "amber",
  PENDING:     "amber",
  FAILED:      "rose",
  EXPIRED:     "rose",
  BLOCKED:     "rose",
  REJECTED:    "rose",
  NOT_STARTED: "slate",
};

function toneClasses(t) {
  return {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50  text-amber-800  border-amber-200",
    rose:    "bg-rose-50   text-rose-700   border-rose-200",
    slate:   "bg-foreground/5 text-foreground/70 border-border",
  }[t] || "bg-foreground/5 text-foreground/70 border-border";
}

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

export default function AdminKycPanel({ userId, token, onChanged }) {
  const [data, setData]         = useState(null);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");
  const [showRaw, setShowRaw]   = useState(false);
  const [actionDlg, setDlg]     = useState(null);   // {action, reason}

  async function load() {
    setErr("");
    try {
      const r = await fetch(`${API}/admin/kyc/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        const txt = await r.text();
        throw new Error(`HTTP ${r.status} — ${txt.slice(0, 120)}`);
      }
      setData(await r.json());
    } catch (e) {
      setErr(String(e?.message || e));
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (userId && token) load(); }, [userId, token]);

  async function doAction(action, reason) {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API}/admin/kyc/${userId}/${action}`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ reason }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.detail?.message || j?.detail || `HTTP ${r.status}`);
      }
      setDlg(null);
      await load();
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  if (!data) {
    return (
      <div className="text-[12.5px] text-foreground/60 py-3" data-testid="admin-kyc-loading">
        {err ? <><AlertCircle className="w-3.5 h-3.5 inline mr-1"/>{err}</> : "Loading KYC data…"}
      </div>
    );
  }

  const kyc      = data.wyzia_kyc || {};
  const status   = (kyc.status || "NOT_STARTED").toUpperCase();
  const tone     = STATUS_TONE[status] || "slate";
  const isDemo   = data.user?.is_demo;
  const acct     = kyc.account_details || {};
  const raw      = kyc.raw_response    || {};
  const mer      = kyc.merchant_data   || {};
  const timeline = data.audit_timeline || [];

  return (
    <div className="space-y-3" data-testid="admin-kyc-panel">
      {/* Header / status */}
      <div className="rounded-xl border border-border bg-white p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-foreground/60"/>
            <div className="text-[12.5px] font-medium">KYC status</div>
          </div>
          <span
            className={`text-[10.5px] mono uppercase tracking-wider px-2 py-1 rounded-full border ${toneClasses(tone)}`}
            data-testid="admin-kyc-status-chip"
          >
            {status}
          </span>
        </div>
        <Kv label="Request ID" value={kyc.request_id || "—"} mono/>
        <Kv label="KYC type"   value={kyc.kyc_type   || "—"}/>
        <Kv label="Trigger"    value={kyc.trigger || (kyc.manual_override ? "admin override" : "manual")}/>
        <Kv label="Started"    value={fmt(kyc.started_at)}/>
        {kyc.completed_at && <Kv label="Completed" value={fmt(kyc.completed_at)}/>}
        {kyc.manual_override && (
          <div className="rounded-lg bg-foreground/5 border border-border p-2.5 mt-2">
            <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 mb-0.5">
              Manual override on file
            </div>
            <div className="text-[12px] text-foreground/75 leading-snug">
              {kyc.override_reason || "No reason recorded."}
            </div>
            <div className="text-[10.5px] mono text-foreground/45 mt-1">
              By {kyc.override_actor || "—"} · {fmt(kyc.override_at)}
            </div>
          </div>
        )}
        {data.user?.kyc_enforced_at && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 mt-2">
            <div className="text-[10.5px] mono uppercase tracking-wider text-amber-700 mb-0.5 flex items-center gap-1">
              <Lock className="w-3 h-3"/>Gate enforced
            </div>
            <div className="text-[12px] text-foreground/75 leading-snug">
              {data.user.kyc_enforced_reason || "Forced by admin — grandfather override active."}
            </div>
            <div className="text-[10.5px] mono text-foreground/45 mt-1">
              By {data.user.kyc_enforced_by || "—"} · {fmt(data.user.kyc_enforced_at)}
            </div>
          </div>
        )}
        {isDemo && (
          <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/45 inline-flex items-center gap-1 pt-1">
            <AlertCircle className="w-2.5 h-2.5"/>demo account · auto-passes gate
          </div>
        )}
        {kyc.kyc_url && (
          <a
            href={kyc.kyc_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-kyc-open-url"
            className="inline-flex items-center gap-1 text-[11.5px] mono uppercase tracking-wider text-foreground hover:underline mt-1"
          >
            Open hosted KYC URL <ExternalLink className="w-3 h-3"/>
          </a>
        )}
      </div>

      {/* Bank account details (mirrored from the rail) */}
      {Object.keys(acct).length > 0 && (
        <div className="rounded-xl border border-border bg-white p-4 space-y-1.5" data-testid="admin-kyc-account-details">
          <div className="text-[12.5px] font-medium mb-1">Linked bank account</div>
          <Kv label="Account holder" value={acct.accountHolderName || acct.account_holder_name || "—"}/>
          <Kv label="Account #"      value={acct.number ? `••••${String(acct.number).slice(-4)}` : "—"} mono/>
          <Kv label="Routing #"      value={acct.routingNumber || acct.routing_number || "—"} mono/>
          <Kv label="Currency"       value={acct.currency || "USD"}/>
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2">
        <ActionBtn
          icon={Check}
          tone="emerald"
          label="Approve"
          disabled={status === "SUCCESS" || isDemo || busy}
          onClick={() => setDlg({ action: "approve", reason: "" })}
          testid="admin-kyc-approve-btn"
        />
        <ActionBtn
          icon={X}
          tone="rose"
          label="Reject"
          disabled={status === "FAILED" || isDemo || busy}
          onClick={() => setDlg({ action: "reject", reason: "" })}
          testid="admin-kyc-reject-btn"
        />
        <ActionBtn
          icon={RefreshCw}
          tone="slate"
          label="Reset (wipe)"
          disabled={!kyc.status || isDemo || busy}
          onClick={() => setDlg({ action: "reset", reason: "" })}
          testid="admin-kyc-reset-btn"
        />
        <ActionBtn
          icon={Lock}
          tone="amber"
          label="Enforce gate"
          disabled={isDemo || busy}
          onClick={() => setDlg({ action: "enforce", reason: "" })}
          testid="admin-kyc-enforce-btn"
        />
      </div>

      {err && (
        <div className="text-[12px] text-rose-700 flex items-center gap-1" data-testid="admin-kyc-err">
          <AlertCircle className="w-3.5 h-3.5"/>{err}
        </div>
      )}

      {/* Action dialog */}
      {actionDlg && (
        <div
          className="fixed inset-0 z-[110] bg-foreground/60 backdrop-blur-sm grid place-items-center px-4"
          data-testid="admin-kyc-dialog"
          role="dialog"
        >
          <div className="bg-background rounded-2xl border-2 border-border w-full max-w-md p-6 shadow-2xl">
            <div className="text-[14px] font-semibold mb-1 capitalize">
              {actionDlg.action} KYC
            </div>
            <p className="text-[12px] text-foreground/65 mb-3 leading-snug">
              {actionDlg.action === "approve" && "Manually mark KYC as SUCCESS. Use only when out-of-band evidence (signed docs, call-back) satisfies KYC."}
              {actionDlg.action === "reject"  && "Manually mark KYC as FAILED. Use when an external signal (sanctions hit, fraud) disqualifies the user."}
              {actionDlg.action === "reset"   && "Wipe wyzia_kyc so the user restarts the flow. They'll need to click Start verification again."}
              {actionDlg.action === "enforce" && "Force the portal blocker ON. Use for periodic recertification of legacy users that were grandfathered in."}
            </p>
            <label className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 block mb-1">
              Reason (required, ≥3 chars)
            </label>
            <textarea
              value={actionDlg.reason}
              onChange={(e) => setDlg({ ...actionDlg, reason: e.target.value })}
              data-testid="admin-kyc-dialog-reason"
              className="w-full min-h-[80px] rounded-lg border border-border bg-secondary/30 p-2.5 text-[13px] mb-3"
              placeholder="e.g. Phone-verified by ops · ticket #1234"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDlg(null)}
                className="px-3.5 py-1.5 text-[12px] mono uppercase tracking-wider rounded-full hover:bg-foreground/5"
                data-testid="admin-kyc-dialog-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => doAction(actionDlg.action, actionDlg.reason.trim())}
                disabled={busy || actionDlg.reason.trim().length < 3}
                className="px-3.5 py-1.5 text-[12px] mono uppercase tracking-wider rounded-full bg-foreground text-background disabled:opacity-40"
                data-testid="admin-kyc-dialog-confirm"
              >
                {busy ? "Working…" : `Confirm ${actionDlg.action}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Raw payload (collapsible) */}
      {(Object.keys(raw).length > 0 || Object.keys(mer).length > 0 || kyc.create_response) && (
        <div className="rounded-xl border border-border bg-foreground/[0.02] p-3" data-testid="admin-kyc-raw">
          <button
            onClick={() => setShowRaw(s => !s)}
            className="w-full flex items-center justify-between text-[11.5px] mono uppercase tracking-wider text-foreground/65 hover:text-foreground"
            data-testid="admin-kyc-raw-toggle"
          >
            <span>Raw verification payload (forensics)</span>
            {showRaw ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
          </button>
          {showRaw && (
            <pre className="text-[10.5px] mono text-foreground/70 mt-2 max-h-[300px] overflow-auto bg-background rounded p-2.5">
{JSON.stringify({ raw_response: raw, merchant_data: mer, create_response: kyc.create_response }, null, 2)}
            </pre>
          )}
        </div>
      )}

      {/* Audit timeline */}
      <div className="rounded-xl border border-border bg-white p-3" data-testid="admin-kyc-timeline">
        <div className="flex items-center gap-1.5 text-[11.5px] mono uppercase tracking-wider text-foreground/65 mb-2">
          <Clock className="w-3.5 h-3.5"/>Audit timeline ({timeline.length})
        </div>
        {timeline.length === 0 ? (
          <div className="text-[12px] text-foreground/55 py-2">No events yet.</div>
        ) : (
          <ul className="space-y-1.5 max-h-[280px] overflow-auto">
            {timeline.map((t) => (
              <li
                key={t.id}
                className="text-[11.5px] flex items-start gap-2 py-1 border-b border-border/40 last:border-b-0"
                data-testid={`admin-kyc-timeline-${t.action}`}
              >
                <span className={`shrink-0 w-2 h-2 rounded-full mt-1.5 ${{
                  admin_approve:  "bg-emerald-500",
                  admin_reject:   "bg-rose-500",
                  admin_reset:    "bg-slate-400",
                  admin_enforce:  "bg-amber-500",
                  webhook_update: "bg-foreground/60",
                  poll_update:    "bg-foreground/40",
                  user_started:   "bg-foreground/80",
                }[t.action] || "bg-foreground/30"}`}/>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-foreground/90">
                    {t.action.replace(/_/g, " ")}
                    {t.prev_status && t.new_status && (
                      <span className="text-foreground/45 ml-1">
                        · {t.prev_status} → {t.new_status}
                      </span>
                    )}
                  </div>
                  <div className="text-foreground/55">
                    <UserIcon className="w-2.5 h-2.5 inline mr-0.5"/>{t.actor_role}
                    {t.actor_id && t.actor_role === "admin" && (
                      <span className="mono ml-1">({String(t.actor_id).slice(0, 8)})</span>
                    )}
                    <span className="mx-1">·</span>{fmt(t.created_at)}
                  </div>
                  {t.reason && (
                    <div className="text-foreground/70 italic mt-0.5">“{t.reason}”</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Kv({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12px]">
      <span className="text-foreground/55">{label}</span>
      <span className={mono ? "mono text-foreground/85" : "text-foreground/85"}>{value}</span>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, tone, disabled, onClick, testid }) {
  const t = {
    emerald: "border-emerald-300 hover:bg-emerald-50",
    rose:    "border-rose-300    hover:bg-rose-50",
    slate:   "border-border      hover:bg-foreground/5",
    amber:   "border-amber-300   hover:bg-amber-50",
  }[tone] || "border-border hover:bg-foreground/5";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex items-center justify-center gap-1.5 text-[11.5px] mono uppercase tracking-wider px-3 py-2 rounded-full border bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${t}`}
    >
      <Icon className="w-3.5 h-3.5"/>{label}
    </button>
  );
}
